import { useEffect, useState } from 'react';
import {
  FileUploadOutput,
  getExtraDetails,
  getWorkspaceId,
  useAppSelector,
  useLazyGetChannelDetailsQuery,
  useLazyGetInvoiceParsedDataQuery,
} from '@zonofi/zono-money-store';
import { isEmpty, isNil } from 'lodash';
import { useParams } from 'react-router-dom';
import { formatDateToDDMMYYYY } from '../../helper';
import { useUploadAndGetPresignedUrl } from '../../hooks';
import { v4 as uuidv4 } from 'uuid';

const SECTION_FIELD_KEYS: Record<string, string[]> = {
  dispatch: ['dispatchDocNo', 'dispatchDate', 'totalBox', 'totalWeight'],
  transporter: [
    'transporterName',
    'vehicleNumber',
    'driverName',
    'driverPhone',
  ],
  destination: ['cityName', 'Pincode', 'address'],
  terms: ['termsOfDelivery'],
};

interface useParseInvoiceProps {
  selectedMedia: FileUploadOutput | undefined;
  imageParseState: {
    shouldParseImage: boolean;
    imageIdToParse?: number | undefined;
  };
  setFormData: React.Dispatch<
    React.SetStateAction<Record<string, string | string[]>>
  >;
  setOpenSections: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  type?: 'upload-invoice' | 'ready-to-dispatch';
}

export const useParseInvoice = ({
  selectedMedia,
  imageParseState,
  setFormData,
  setOpenSections,
  type = 'upload-invoice',
}: useParseInvoiceProps) => {
  const { customerId = '' } = useParams();
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const customerDetails = useAppSelector(getExtraDetails);

  const [channelId, setChannelId] = useState<string>('');

  const [channelDetailsApi, channelDetailsResponse] =
    useLazyGetChannelDetailsQuery();
  const [fetchInvoiceParsedData, invoiceParsedDataResponse] =
    useLazyGetInvoiceParsedDataQuery();
  const { uploadAndGetPresignedUrl } = useUploadAndGetPresignedUrl({
    customerId: customerId || '',
  });

  useEffect(() => {
    const payload = {
      workspaceId: sellerWorkspaceId,
      sellerWorkspaceId: sellerWorkspaceId,
      customerId: customerId,
      channelType: 'C',
    };
    channelDetailsApi(payload);
  }, []);

  useEffect(() => {
    if (channelDetailsResponse.isSuccess && channelDetailsResponse.data?.id) {
      setChannelId(channelDetailsResponse.data?.id);
    }
  }, [channelDetailsResponse]);

  useEffect(() => {
    const parseInvoice = async () => {
      if (
        !isEmpty(selectedMedia) &&
        channelId &&
        imageParseState?.shouldParseImage
      ) {
        setFormData({});
        setOpenSections({});

        const presignedUrl = await uploadAndGetPresignedUrl(selectedMedia!);

        if (!presignedUrl) {
          console.error('Unable to upload invoice file and resolve URL');
          return;
        }

        const contentType =
          selectedMedia?.type ||
          selectedMedia?.file?.type ||
          'application/octet-stream';

        fetchInvoiceParsedData({
          sellerWorkspaceId: sellerWorkspaceId,
          body: {
            activity: {
              type: 'upload',
              intent: 'invoice',
              conversation: { id: uuidv4() },
              channelId,
              inviteId: customerId,
              workspaceId: sellerWorkspaceId,
              text: 'Upload Invoice',
              file: {
                fileName:
                  selectedMedia?.file?.name || selectedMedia?.name || '',
                mimeType: contentType,
                extension: '',
                url: presignedUrl,
              },
            },
          },
        });
      }
    };

    parseInvoice();
  }, [
    selectedMedia,
    channelId,
    imageParseState?.shouldParseImage,
    sellerWorkspaceId,
    customerId,
  ]);

  useEffect(() => {
    if (invoiceParsedDataResponse.isSuccess && invoiceParsedDataResponse.data) {
      const parsed = invoiceParsedDataResponse.data;

      // map parsed → formData
      const updatedFormData: Record<string, string | string[]> = {
        invoiceNumber: parsed.invoiceNumber ?? '',
        invoiceAmount: parsed.invoiceAmount ?? '',
        invoiceDate: formatDateToDDMMYYYY(parsed.invoiceDate),
        dueDate: formatDateToDDMMYYYY(parsed.dueDate),
        dispatchDate: formatDateToDDMMYYYY(parsed.deliveryNoteDate),
        note: parsed.otherReferences ?? '',
        address: parsed.destination ?? '',
        Pincode: parsed.Pincode ?? '',
        dispatchDocNo: parsed.dispatchDocNo ?? '',
        termsOfDelivery: parsed.termsOfDelivery ?? '',
        ...(type === 'ready-to-dispatch' && customerDetails?.orderNumId
          ? { orderNumbers: [customerDetails.orderNumId] }
          : {}),
      };
      setFormData((prev) => ({
        ...prev,
        ...updatedFormData,
      }));

      const newOpenSections: Record<string, boolean> = {};
      Object.entries(SECTION_FIELD_KEYS).forEach(([sectionKey, fieldKeys]) => {
        const hasAnyValue = fieldKeys.some((fieldKey) => {
          const value = updatedFormData[fieldKey];
          return !isNil(value) && !isEmpty(value);
        });

        newOpenSections[sectionKey] = hasAnyValue;
      });
      setOpenSections(newOpenSections);
    }
  }, [invoiceParsedDataResponse]);

  useEffect(() => {
    if (invoiceParsedDataResponse.isError) {
      setFormData({});
      setOpenSections({});
    }
  }, [invoiceParsedDataResponse]);

  return {
    isLoading:
      invoiceParsedDataResponse?.isLoading ||
      invoiceParsedDataResponse?.isFetching,
    channelId,
  };
};
